import { ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  AutocompleteRenderInputParams,
  Box,
  GroupedSearchDropdown,
  GroupedSearchDropdownProps,
  Modal,
  Svg,
} from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { Text } from '@rideiq/shared/components/Text';
import { colorTokens } from '@rideiq/shared/components/variables';
import { includes } from 'lodash';
import styled from 'styled-components';

import { analyticsCreateLocation } from '../../../../api/services/Analytics';
import { DefaultLocationFormData, LocationFormData } from '../../../types';
import { LocationForm } from '../LocationForm';

type Option = {
  group: string;
  label: string;
  [x: string]: string;
};

export interface SelectLocationProps
  extends Omit<GroupedSearchDropdownProps, 'inputValue' | 'setInputValue' | 'isLoading'> {
  id?: string;
  hidden: boolean;
  options: Option[];
  calendarSlug?: string;
  setInputValue: (location?: Nullable<Option>) => void;
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
  saveLocation: (data: LocationFormData) => Promise<AccountLocation | null>;
  renderOption?: (props, option) => JSX.Element | ReactElement;
  disableOptions?: string[];
  source?: string;
  inputValue?: string;
  clearOnSelect?: boolean;
  clearOnCancel?: boolean;
  defaultsForNewLocation?: DefaultLocationFormData;
  disabledOptionKey?: string;
}

// Make sure to use with useLocationOptions hook in parent component
// const { locations, debounceHandleQuery } = useLocationOptions();

export const defaultLocationState: DefaultLocationFormData = {
  address: '',
  pickupProcedureInstructions: '',
  dropoffProcedureInstructions: '',
  calendarSlug: '',
  placeId: '',
  locationType: '',
};

export const SelectLocation = ({
  id,
  hidden,
  options,
  calendarSlug,
  setInputValue,
  saveLocation,
  renderOption,
  disableOptions,
  source,
  inputValue,
  clearOnSelect = true,
  clearOnCancel = false,
  disabledOptionKey = 'placeId',
  defaultsForNewLocation,
  ...props
}: SelectLocationProps) => {
  const [locationInputValue, setLocationInputValue] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLocation, setNewLocation] = useState<LocationFormData>();
  const [isSaving, setIsSaving] = useState(false);
  const newLocationGroupName = 'Create a new location:';

  useEffect(() => {
    inputValue && setLocationInputValue(inputValue);
  }, [inputValue]);

  const handleAddNewLocation = async (data: Omit<LocationFormData, 'preciseLocation'>) => {
    setIsSaving(true);

    const { calendarSlug: newCalendarSlug, ...rest } = data;

    const locationNewData = {
      ...(newCalendarSlug && { calendarSlug: newCalendarSlug }),
      ...rest,
    };
    const location = await saveLocation(locationNewData);

    if (location) {
      setInputValue({
        ...data,
        calendarSlug: data.calendarSlug || '',
        id: location.id.toString(),
        group: newLocationGroupName,
        label: newLocation?.address || location.address || location.name || '',
        placeId: location.placeId || '',
        latLng: `${location.lat},${location.lng}`,
        preciseLatLng: location.preciseLocation?.lat
          ? `${location.preciseLocation.lat},${location.preciseLocation.lng}`
          : '',
        calendar: location.calendar?.slug || '',
      });
      setIsSaving(false);
      setIsModalOpen(false);
      analyticsCreateLocation(location.name, !!location.calendar?.slug, !!location.preciseLocation?.lat, source);
    }
    setIsSaving(false);
  };

  return (
    <>
      <GroupedSearchDropdown
        ListboxProps={{ 'aria-label': 'Locations', 'aria-labelledby': undefined }}
        sx={{ minWidth: '275px' }}
        id={id}
        hidden={hidden}
        options={options}
        isLoading={false}
        getOptionDisabled={(option) => includes(disableOptions, option[disabledOptionKey])}
        inputValue={locationInputValue}
        setInputValue={(value) => {
          const location = options.find((o) => o.label === value);

          if (location?.group != newLocationGroupName) {
            setInputValue(location);
          } else {
            const defaults = defaultsForNewLocation || defaultLocationState;
            const hasNumericDigits = /\d/.test(location.name || '');

            setNewLocation({
              ...defaults,
              name: hasNumericDigits ? '' : location.name,
              address: location.address || location.label,
              placeId: location.placeId || '',
              calendarSlug: location.calendar || '',
            });
          }
          setLocationInputValue(value);
        }}
        onChange={(_, value) => {
          if (!value || typeof value === 'string') {
            return;
          }
          if (value.group === newLocationGroupName) {
            setIsModalOpen(true);
          }
          setLocationInputValue(clearOnSelect ? '' : value.label);
        }}
        boldCharacterMatch={true}
        {...(renderOption && { renderOption })}
        {...props}
      />
      {isModalOpen && (
        <Presentation
          defaultCalendarSlug={calendarSlug}
          newLocation={newLocation}
          isSaving={isSaving}
          onSubmit={handleAddNewLocation}
          onClose={() => {
            setIsModalOpen(false);
            setNewLocation(undefined);

            if (clearOnCancel) {
              setLocationInputValue('');
              setInputValue(undefined);
            }
          }}
        />
      )}
    </>
  );
};

const Presentation = ({ defaultCalendarSlug, newLocation, isSaving, onSubmit, onClose }) => {
  const [isSettingPin, setIsSettingPin] = useState(false);

  return (
    <Modal
      open={true}
      data-testid="create-location-modal"
      maxWidth="xl"
      PaperProps={{
        style: {
          maxWidth: 1195,
        },
      }}
    >
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="0.5rem">
          <Box display="flex" flexDirection={'column'} gap="0.375rem">
            <Text variant="h1" fontWeight={700}>
              Create Location
            </Text>
            <Text textColor={colorTokens.text.medium}>The location will be available to be assigned to any rider.</Text>
          </Box>
          <CloseButton onClick={onClose} data-testid="closeButton" aria-label="Close rider add location modal">
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content sx={{ padding: '8px 24px 24px' }}>
        <LocationForm
          isModal={true}
          calendarSlug={defaultCalendarSlug}
          initialValues={newLocation}
          onSubmit={onSubmit}
          onCancel={onClose}
          onSettingPin={(isSetting) => setIsSettingPin(isSetting)}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button color="tertiary" onClick={onClose} loading={isSaving || isSettingPin}>
          Cancel
        </Button>
        <Button type="submit" color="primary" form={'new-location-form'} loading={isSaving || isSettingPin}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const CloseButton = styled('div')`
  display: inline-flex;
  margin-left: auto;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background: #ddebfa;
    svg {
      color: ${colorTokens.text.hover};
    }
  }
`;
